/**
 * Loads org branding (colors, logos) before the rest of the app renders.
 */
import * as React from 'react';
import { getPublicBranding } from '@/services/branding.service';
import { getErrorMessage } from '@/utils/errors-utils';
import { useThemeStore } from '@/stores/themeStore';

/** Props for {@link BrandingProvider}. */
interface BrandingProviderProps {
  children: React.ReactNode;
}

/** Fetches org branding in the background without a spinner, to avoid doubling AuthProvider. */
export const BrandingProvider: React.FC<BrandingProviderProps> = ({ children }) => {
  const [error, setError] = React.useState<string | null>(null);
  const setBranding = useThemeStore((state) => state.setBranding);
  // useRef persists across React StrictMode's fake unmount/remount cycle in dev,
  // preventing the double-invoke, while resetting on each real page load so
  // branding data is always fetched fresh from the DB.
  const hasFetched = React.useRef(false);

  React.useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const loadBranding = async () => {
      try {
        const data = await getPublicBranding();
        setBranding(data);
      } catch (err) {
        console.error('Error loading branding:', err);
        setError(getErrorMessage(err));
        // Continue anyway with default branding
      }
    };

    void loadBranding();
  }, [setBranding]);

  if (error) {
    console.warn('Branding failed to load, using defaults:', error);
  }

  return <>{children}</>;
};
